import isNil from 'lodash/isNil';
import * as actions from './NetWorthSidebarActions';
import { UPDATE_APEX_ACCOUNT_STATUS } from '../../Apex/StatusViews/StatusViews.meta';

const initialState = {
  errors: [],
  isFetching: false,
  accountGroups: [],
  managedAccountGroups: {},
  accountTypesDictionary: {},
  otherAssetTypesDictionary: {},
  totalNetWorth: 0,
  accounts: {},
  // this is used as a key in components that need to react to data changes in the widget
  dataVersion: 0,
  cachedAccountsEnrichedData: [],
  allowAddManualAccount: false,
};

const enrichAccountGroups = (accountGroups, enrichedAccountsList) => {
  return accountGroups.map(accountGroup => ({
    ...accountGroup,
    Items: accountGroup.Items.map(account => {
      if (account.Item && account.Item.AccountId) {
        return enrichedAccountsList[account.Item.AccountId]
          ? { ...account, ...enrichedAccountsList[account.Item.AccountId] }
          : account;
      }

      return account;
    }),
  }));
};

export default (state = initialState, { type, payload }) => {
  switch (type) {
    case actions.ERRORS_SET:
      return { ...state, errors: payload };
    case actions.IS_FETCHING_NETWORTH_DATA_SET:
      return {
        ...state,
        isFetching: payload.isFetching,
        dataVersion:
          payload.isFetching && !payload.isInitialFetch ? state.dataVersion + 1 : state.dataVersion,
      };
    case actions.ORION_ACCOUNTS_ENRICH:
      return {
        ...state,
        accountGroups: enrichAccountGroups(state.accountGroups, payload),
        cachedAccountsEnrichedData: payload,
      };
    case actions.NETWORTH_DATA_SET: {
      const {
        EditableAccountTypes: accountTypesDictionary,
        EditableOtherAssetTypes: otherAssetTypesDictionary,
        AllowAddManualAccount: allowAddManualAccount,
      } = payload.Options;

      const items = isNil(payload.Items.Investment)
        ? payload.Items
        : {
            ...payload.Items,
            Investment: {
              ...payload.Items.Investment,
              Items: payload.Items.Investment.Items.reduce(
                (acc, accountOverview) => [
                  ...acc,
                  ...(accountOverview.Groups.length > 0
                    ? accountOverview.Groups.map(group => ({
                        ...accountOverview,
                        GroupName: group.RegistrationNumber
                          ? [group.Name, group.RegistrationNumber]
                          : [group.Name],
                        GroupId: group.Id,
                      }))
                    : [accountOverview]),
                ],
                [],
              ),
            },
          };

      const accountGroups = Object.keys(items).reduce((groups, accountGroupName) => {
        const accountGroup = items[accountGroupName];

        if (accountGroup.Items.length === 0) {
          return groups;
        }

        // if the first item is an asset, all are
        const isAsset =
          !isNil(accountGroup.Items[0].Item) &&
          (!isNil(accountGroup.Items[0].Item.OtherAssetType) || accountGroup.Items[0].Item.IsAsset);

        // mark accounts that are not supported
        const formattedAccounts = accountGroup.Items.map(account => {
          let isEditable = false;

          // APEX accounts
          // Does not have { Item: {}}
          if (isNil(account.Item)) {
            return {
              ...account,
              IsEditable: true,
              IsOrion: account.IsApexOrion,
              Value: account.Value,
            };
          }

          if (!isNil(account.Item.AccountType)) {
            isEditable = !!accountTypesDictionary[account.Item.AccountType];
          } else if (!isNil(account.Item.OtherAssetType)) {
            isEditable = !!otherAssetTypesDictionary[account.Item.OtherAssetType];
          }

          return {
            ...account,
            IsEditable: isEditable,
            IsOrion: account.Item.IsOrion,
            Value: isAsset ? account.Value : -1 * account.Value,
          };
        });

        return [
          ...groups,
          {
            ...accountGroup,
            Name: accountGroupName,
            Items: formattedAccounts,
            Total: isAsset ? accountGroup.Total : -1 * accountGroup.Total,
          },
        ];
      }, []);

      const accounts = Object.values(items).reduce((acc, { Items }) => {
        const accountsDictionary = Items.reduce((acc2, account) => {
          const {
            Item,
            ApexStatus,
            IsApexOrion = false,
            ManagedAccountId,
            IsClosed = false,
          } = account;
          if (Item && Item.AccountId) {
            return {
              ...acc2,
              [Item.AccountId]: {
                ...Item,
                ApexStatus,
                IsApexOrion,
                ManagedAccountId,
                IsClosed,
              },
            };
          }
          return acc2;
        }, {});

        return { ...acc, ...accountsDictionary };
      }, {});

      const accountGroupsEnriched = enrichAccountGroups(
        accountGroups,
        state.cachedAccountsEnrichedData,
      );

      return {
        ...state,
        accountGroups: accountGroupsEnriched,
        accounts,
        totalNetWorth: payload.NetWorth,
        allowAddManualAccount,
        isFetching: false,
        unmanagedAccountsLabel: payload.UnmanagedAccountsLabel,
      };
    }
    case UPDATE_APEX_ACCOUNT_STATUS: {
      if (state.accountGroups.length === 0) {
        return state;
      }

      const updatedAccounts = state.accounts[payload.accountId]
        ? {
            ...state.accounts,
            [payload.accountId]: {
              ...state.accounts[payload.accountId],
              ApexStatus: payload.ApexStatus,
            },
          }
        : state.accounts;

      const updatedAccountGroups = state.accountGroups.map(accountGroup => {
        const updatedAccountGroupItem = accountGroup.Items.map(accountGroupItem => {
          const { ManagedAccountId, Item } = accountGroupItem;

          if (
            ManagedAccountId === payload.accountId ||
            (Item && Item.AccountId === payload.accountId)
          ) {
            return {
              ...accountGroupItem,
              ApexStatus: payload.ApexStatus,
            };
          }
          return accountGroupItem;
        });

        return {
          ...accountGroup,
          Items: updatedAccountGroupItem,
        };
      });

      return {
        ...state,
        accounts: updatedAccounts,
        accountGroups: updatedAccountGroups,
      };
    }
    default:
      return state;
  }
};
